import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  User,
  UserPlus,
  Key,
  Shield,
  Edit2,
  Check,
  X,
  Eye,
  EyeOff,
  UserX,
  UserCheck,
  AlertCircle,
  Briefcase
} from 'lucide-react';

const PERFIS = [
  { value: 'geral',    label: 'Geral',    cor: 'bg-gray-100 text-gray-700' },
  { value: 'gerente',  label: 'Gerente',  cor: 'bg-amber-100 text-amber-700' },
  { value: 'admin',    label: 'Admin',    cor: 'bg-purple-100 text-purple-700' },
];

const badgePerfil = (perfil) => {
  const p = PERFIS.find(x => x.value === perfil) || PERFIS[0];
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${p.cor}`}>
      {p.label}
    </span>
  );
};

const GerenciarUsuarios = () => {
  const { usuario, usuario: usuarioLogado, isAdmin } = useAuth();
  const [usuarios, setUsuarios] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);
  const [sucesso, setSucesso] = useState(null);

  // Modais
  const [modalNovoUsuario, setModalNovoUsuario] = useState(false);
  const [modalAlterarSenha, setModalAlterarSenha] = useState(false);
  const [modalEditarUsuario, setModalEditarUsuario] = useState(false);
  const [usuarioSelecionado, setUsuarioSelecionado] = useState(null);

  // Formulário novo usuário
  const [novoUsuario, setNovoUsuario] = useState({
    username: '',
    senha: '',
    confirmarSenha: '',
    nome_completo: '',
    email: '',
    tipo: 'usuario',
    perfil: 'geral'
  });

  // Formulário editar usuário
  const [editarUsuario, setEditarUsuario] = useState({
    id: null,
    username: '',
    nome_completo: '',
    email: '',
    tipo: 'usuario',
    perfil: 'geral',
    ativo: true
  });

  // Formulário alterar senha
  const [alterarSenha, setAlterarSenha] = useState({
    senha_atual: '',
    senha_nova: '',
    confirmar_senha_nova: ''
  });

  const [mostrarSenhas, setMostrarSenhas] = useState({
    senha: false, confirmar: false, atual: false, nova: false, confirmarNova: false
  });

  useEffect(() => {
    if (usuario?.perfil === 'admin') carregarUsuarios();
  }, []);

  const getAxios = async () => {
    const axios = (await import('axios')).default;
    const token = localStorage.getItem('token');
    return { axios, headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } };
  };

  const carregarUsuarios = async () => {
    try {
      setCarregando(true);
      setErro(null);
      const { axios, headers } = await getAxios();
      const response = await axios.get('/api/auth/usuarios', { headers });
      if (response.data.sucesso) setUsuarios(response.data.dados);
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
      setErro('Erro ao carregar lista de usuários');
    } finally {
      setCarregando(false);
    }
  };

  const mostrarSucesso = (msg) => {
    setSucesso(msg);
    setTimeout(() => setSucesso(null), 3000);
  };

  // ── CRIAR ──────────────────────────────────────────────
  const handleCriarUsuario = async (e) => {
    e.preventDefault();
    setErro(null);

    if (!novoUsuario.username || !novoUsuario.senha || !novoUsuario.nome_completo) {
      return setErro('Por favor, preencha todos os campos obrigatórios');
    }
    if (novoUsuario.senha.length < 6) return setErro('A senha deve ter no mínimo 6 caracteres');
    if (novoUsuario.senha !== novoUsuario.confirmarSenha) return setErro('As senhas não coincidem');

    try {
      const { axios, headers } = await getAxios();
      const response = await axios.post('/api/auth/usuarios', {
        username: novoUsuario.username,
        senha: novoUsuario.senha,
        nome_completo: novoUsuario.nome_completo,
        email: novoUsuario.email,
        tipo: novoUsuario.tipo,
        perfil: novoUsuario.perfil
      }, { headers });

      if (response.data.sucesso) {
        mostrarSucesso('Usuário criado com sucesso!');
        setModalNovoUsuario(false);
        setNovoUsuario({ username: '', senha: '', confirmarSenha: '', nome_completo: '', email: '', tipo: 'usuario', perfil: 'geral' });
        carregarUsuarios();
      }
    } catch (error) {
      setErro(error.response?.data?.mensagem || 'Erro ao criar usuário');
    }
  };

  // ── EDITAR ─────────────────────────────────────────────
  const handleEditarUsuario = async (e) => {
    e.preventDefault();
    setErro(null);

    if (!editarUsuario.nome_completo) return setErro('Nome completo é obrigatório');

    try {
      const { axios, headers } = await getAxios();
      const response = await axios.put(`/api/auth/usuarios/${editarUsuario.id}`, {
        nome_completo: editarUsuario.nome_completo,
        email: editarUsuario.email,
        tipo: editarUsuario.tipo,
        perfil: editarUsuario.perfil
      }, { headers });

      if (response.data.sucesso) {
        mostrarSucesso('Usuário atualizado com sucesso!');
        setModalEditarUsuario(false);
        carregarUsuarios();
      }
    } catch (error) {
      setErro(error.response?.data?.mensagem || 'Erro ao atualizar usuário');
    }
  };

  // ── ALTERAR SENHA ──────────────────────────────────────
  const handleAlterarSenha = async (e) => {
    e.preventDefault();
    setErro(null);

    if (!alterarSenha.senha_nova || alterarSenha.senha_nova.length < 6)
      return setErro('A nova senha deve ter no mínimo 6 caracteres');
    if (alterarSenha.senha_nova !== alterarSenha.confirmar_senha_nova)
      return setErro('As senhas não coincidem');

    try {
      const { axios, headers } = await getAxios();
      const dados = { senha_nova: alterarSenha.senha_nova };
      if (usuarioSelecionado?.id === usuarioLogado.id) dados.senha_atual = alterarSenha.senha_atual;

      const response = await axios.put(`/api/auth/usuarios/${usuarioSelecionado.id}/senha`, dados, { headers });

      if (response.data.sucesso) {
        mostrarSucesso('Senha alterada com sucesso!');
        setModalAlterarSenha(false);
        setAlterarSenha({ senha_atual: '', senha_nova: '', confirmar_senha_nova: '' });
      }
    } catch (error) {
      setErro(error.response?.data?.mensagem || 'Erro ao alterar senha');
    }
  };

  // ── STATUS ─────────────────────────────────────────────
  const handleAlterarStatus = async (usuario) => {
    try {
      const { axios, headers } = await getAxios();
      const response = await axios.put(`/api/auth/usuarios/${usuario.id}/status`, { ativo: !usuario.ativo }, { headers });
      if (response.data.sucesso) {
        mostrarSucesso(`Usuário ${!usuario.ativo ? 'ativado' : 'desativado'} com sucesso!`);
        carregarUsuarios();
      }
    } catch (error) {
      setErro(error.response?.data?.mensagem || 'Erro ao alterar status');
    }
  };

  const abrirModalEditar = (usuario) => {
    setUsuarioSelecionado(usuario);
    setEditarUsuario({
      id: usuario.id,
      username: usuario.username,
      nome_completo: usuario.nome_completo,
      email: usuario.email || '',
      tipo: usuario.tipo,
      perfil: usuario.perfil || 'geral',
      ativo: usuario.ativo
    });
    setModalEditarUsuario(true);
  };

  const abrirModalSenha = (usuario) => {
    setUsuarioSelecionado(usuario);
    setAlterarSenha({ senha_atual: '', senha_nova: '', confirmar_senha_nova: '' });
    setModalAlterarSenha(true);
  };

  // ── HELPERS DE FORMULÁRIO ──────────────────────────────
  const CampoSenha = ({ label, campo, chave, obrigatorio }) => (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}{obrigatorio && ' *'}</label>
      <div className="relative">
        <input
          type={mostrarSenhas[chave] ? 'text' : 'password'}
          value={campo === 'novo' ? novoUsuario.senha : campo === 'confirmar' ? novoUsuario.confirmarSenha
            : alterarSenha[chave === 'atual' ? 'senha_atual' : chave === 'nova' ? 'senha_nova' : 'confirmar_senha_nova']}
          onChange={(e) => {
            if (campo === 'novo') setNovoUsuario({ ...novoUsuario, senha: e.target.value });
            else if (campo === 'confirmar') setNovoUsuario({ ...novoUsuario, confirmarSenha: e.target.value });
            else if (chave === 'atual') setAlterarSenha({ ...alterarSenha, senha_atual: e.target.value });
            else if (chave === 'nova') setAlterarSenha({ ...alterarSenha, senha_nova: e.target.value });
            else setAlterarSenha({ ...alterarSenha, confirmar_senha_nova: e.target.value });
          }}
          className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          required={obrigatorio}
        />
        <button type="button"
          onClick={() => setMostrarSenhas({ ...mostrarSenhas, [chave]: !mostrarSenhas[chave] })}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
          {mostrarSenhas[chave] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );

  const SelectPerfil = ({ value, onChange }) => (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        <span className="flex items-center gap-1"><Briefcase className="w-4 h-4" /> Perfil</span>
      </label>
      <select value={value} onChange={onChange}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
        {PERFIS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
      </select>
    </div>
  );

  if (usuario?.perfil !== 'admin') {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Shield className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800">Acesso Negado</h2>
          <p className="text-gray-600 mt-2">Apenas administradores podem acessar esta página</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Gerenciar Usuários</h1>
          <p className="text-gray-600 mt-1">Controle de acesso ao sistema</p>
        </div>
        <button onClick={() => setModalNovoUsuario(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
          <UserPlus className="w-5 h-5" /> Novo Usuário
        </button>
      </div>

      {/* Mensagens */}
      {erro && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <span className="text-red-700">{erro}</span>
          <button onClick={() => setErro(null)} className="ml-auto text-red-500 hover:text-red-700"><X className="w-5 h-5" /></button>
        </div>
      )}
      {sucesso && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
          <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
          <span className="text-green-700">{sucesso}</span>
          <button onClick={() => setSucesso(null)} className="ml-auto text-green-500 hover:text-green-700"><X className="w-5 h-5" /></button>
        </div>
      )}

      {/* Tabela */}
      {carregando ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {['Usuário', 'Email', 'Perfil', 'Status', 'Último Acesso', 'Ações'].map(col => (
                  <th key={col} className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${col === 'Ações' ? 'text-right' : 'text-left'}`}>
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {usuarios.map((usuario) => (
                <tr key={usuario.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <User className="w-5 h-5 text-blue-600" />
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">{usuario.nome_completo}</div>
                        <div className="text-sm text-gray-500">@{usuario.username}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{usuario.email || '-'}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {badgePerfil(usuario.perfil || 'geral')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {usuario.ativo ? (
                      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        <UserCheck className="w-3 h-3" /> Ativo
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        <UserX className="w-3 h-3" /> Inativo
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {usuario.ultimo_acesso ? new Date(usuario.ultimo_acesso).toLocaleString('pt-BR') : 'Nunca'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end gap-2">
                      <button onClick={() => abrirModalEditar(usuario)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" title="Editar">
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button onClick={() => abrirModalSenha(usuario)}
                        className="p-2 text-amber-600 hover:bg-amber-50 rounded-lg transition-colors" title="Alterar senha">
                        <Key className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleAlterarStatus(usuario)}
                        className={`p-2 rounded-lg transition-colors ${usuario.ativo ? 'text-red-600 hover:bg-red-50' : 'text-green-600 hover:bg-green-50'}`}
                        title={usuario.ativo ? 'Desativar' : 'Ativar'}>
                        {usuario.ativo ? <UserX className="w-4 h-4" /> : <UserCheck className="w-4 h-4" />}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── MODAL NOVO USUÁRIO ── */}
      {modalNovoUsuario && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-gray-900">Novo Usuário</h3>
                <button onClick={() => setModalNovoUsuario(false)} className="text-gray-400 hover:text-gray-600">
                  <X className="w-6 h-6" />
                </button>
              </div>
              <form onSubmit={handleCriarUsuario} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Usuário (login) *</label>
                  <input type="text" value={novoUsuario.username}
                    onChange={(e) => setNovoUsuario({ ...novoUsuario, username: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nome Completo *</label>
                  <input type="text" value={novoUsuario.nome_completo}
                    onChange={(e) => setNovoUsuario({ ...novoUsuario, nome_completo: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input type="email" value={novoUsuario.email}
                    onChange={(e) => setNovoUsuario({ ...novoUsuario, email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Senha *</label>
                  <div className="relative">
                    <input type={mostrarSenhas.senha ? 'text' : 'password'} value={novoUsuario.senha}
                      onChange={(e) => setNovoUsuario({ ...novoUsuario, senha: e.target.value })}
                      className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" required />
                    <button type="button" onClick={() => setMostrarSenhas({ ...mostrarSenhas, senha: !mostrarSenhas.senha })}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                      {mostrarSenhas.senha ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Mínimo 6 caracteres</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Confirmar Senha *</label>
                  <div className="relative">
                    <input type={mostrarSenhas.confirmar ? 'text' : 'password'} value={novoUsuario.confirmarSenha}
                      onChange={(e) => setNovoUsuario({ ...novoUsuario, confirmarSenha: e.target.value })}
                      className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" required />
                    <button type="button" onClick={() => setMostrarSenhas({ ...mostrarSenhas, confirmar: !mostrarSenhas.confirmar })}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                      {mostrarSenhas.confirmar ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {/* Perfil */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Perfil</label>
                  <select
                    value={novoUsuario.perfil}
                    onChange={(e) => setNovoUsuario({ ...novoUsuario, perfil: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
                  >
                    <option value="geral">Geral</option>
                    <option value="gerente">Gerente</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>

  
                

                <div className="flex gap-3 pt-4">
                  <button type="button" onClick={() => setModalNovoUsuario(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">
                    Cancelar
                  </button>
                  <button type="submit"
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                    Criar Usuário
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* ── MODAL EDITAR USUÁRIO ── */}
      {modalEditarUsuario && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-gray-900">Editar Usuário</h3>
                <button onClick={() => setModalEditarUsuario(false)} className="text-gray-400 hover:text-gray-600">
                  <X className="w-6 h-6" />
                </button>
              </div>
              <form onSubmit={handleEditarUsuario} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Usuário (login)</label>
                  <input type="text" value={editarUsuario.username} disabled
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500" />
                  <p className="text-xs text-gray-500 mt-1">Nome de usuário não pode ser alterado</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nome Completo *</label>
                  <input type="text" value={editarUsuario.nome_completo}
                    onChange={(e) => setEditarUsuario({ ...editarUsuario, nome_completo: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input type="email" value={editarUsuario.email}
                    onChange={(e) => setEditarUsuario({ ...editarUsuario, email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
                </div>

                {/* Perfil */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Perfil</label>
                  <select
                    value={editarUsuario.perfil}
                    onChange={(e) => setEditarUsuario({ ...editarUsuario, perfil: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
                  >
                    <option value="geral">Geral</option>
                    <option value="gerente">Gerente</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>

                <div className="flex gap-3 pt-4">
                  <button type="button" onClick={() => setModalEditarUsuario(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">
                    Cancelar
                  </button>
                  <button type="submit"
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                    Salvar Alterações
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* ── MODAL ALTERAR SENHA ── */}
      {modalAlterarSenha && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-gray-900">Alterar Senha</h3>
                <button onClick={() => setModalAlterarSenha(false)} className="text-gray-400 hover:text-gray-600">
                  <X className="w-6 h-6" />
                </button>
              </div>
              <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-800"><strong>Usuário:</strong> {usuarioSelecionado?.nome_completo}</p>
              </div>
              <form onSubmit={handleAlterarSenha} className="space-y-4">
                {usuarioSelecionado?.id === usuarioLogado.id && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Senha Atual *</label>
                    <div className="relative">
                      <input type={mostrarSenhas.atual ? 'text' : 'password'} value={alterarSenha.senha_atual}
                        onChange={(e) => setAlterarSenha({ ...alterarSenha, senha_atual: e.target.value })}
                        className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" required />
                      <button type="button" onClick={() => setMostrarSenhas({ ...mostrarSenhas, atual: !mostrarSenhas.atual })}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                        {mostrarSenhas.atual ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nova Senha *</label>
                  <div className="relative">
                    <input type={mostrarSenhas.nova ? 'text' : 'password'} value={alterarSenha.senha_nova}
                      onChange={(e) => setAlterarSenha({ ...alterarSenha, senha_nova: e.target.value })}
                      className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" required />
                    <button type="button" onClick={() => setMostrarSenhas({ ...mostrarSenhas, nova: !mostrarSenhas.nova })}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                      {mostrarSenhas.nova ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Mínimo 6 caracteres</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Confirmar Nova Senha *</label>
                  <div className="relative">
                    <input type={mostrarSenhas.confirmarNova ? 'text' : 'password'} value={alterarSenha.confirmar_senha_nova}
                      onChange={(e) => setAlterarSenha({ ...alterarSenha, confirmar_senha_nova: e.target.value })}
                      className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" required />
                    <button type="button" onClick={() => setMostrarSenhas({ ...mostrarSenhas, confirmarNova: !mostrarSenhas.confirmarNova })}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                      {mostrarSenhas.confirmarNova ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div className="flex gap-3 pt-4">
                  <button type="button" onClick={() => setModalAlterarSenha(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">
                    Cancelar
                  </button>
                  <button type="submit"
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                    Alterar Senha
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GerenciarUsuarios;